/**
 * Video chapters — the pure half of N02 (roadmap batch 4).
 *
 * The backend hands a video `chapters: [{ time, name }]` (seconds from the
 * start, a label). Two consumers read it: the OTT detail's chapter list, whose
 * rows seek the player, and the player itself, which takes chapters as a
 * WebVTT text track of kind `chapters` — that is the one shape the bundled
 * video.js + Nuevo build understands (the chapters menu, and Nuevo's
 * progress-bar markers + hover titles, both come from that track; read off
 * the bundle 2026-09-12). Both consumers go through `normalizeChapters` so the
 * list and the track can never disagree about which chapters exist.
 *
 * Kept free of React and DOM so the rules are unit-tested: a malformed chapter
 * silently dropping the whole menu is the kind of failure nobody reports.
 */

export type Chapter = { time: number; name: string };

/** What the API may hand us — every field nullable on the live schema. */
export type RawChapter = { time?: number | null; name?: string | null } | null | undefined;

/** A usable duration: finite and positive, else null (unknown). */
function knownDuration(duration: number | null | undefined): number | null {
  return typeof duration === "number" && Number.isFinite(duration) && duration > 0
    ? duration
    : null;
}

/**
 * Valid chapters, in time order, one per second mark. Dropped: a missing or
 * blank name, a non-finite or negative time, a time at or past a KNOWN
 * duration (a chapter the video can never reach), and a second occupied by an
 * earlier entry. Times are floored to whole seconds — the API stores integers
 * and a marker drawn at 61.4 s and one at 61.9 s would be one marker.
 */
export function normalizeChapters(
  chapters: readonly RawChapter[] | null | undefined,
  duration?: number | null,
): Chapter[] {
  const max = knownDuration(duration);
  const seen = new Set<number>();
  const out: Chapter[] = [];
  for (const c of chapters ?? []) {
    if (!c) continue;
    const name = (c.name ?? "").replace(/\s+/g, " ").trim();
    const raw = typeof c.time === "number" ? c.time : Number.NaN;
    if (!name || !Number.isFinite(raw) || raw < 0) continue;
    const time = Math.floor(raw);
    if (max !== null && time >= max) continue;
    if (seen.has(time)) continue;
    seen.add(time);
    out.push({ time, name });
  }
  return out.sort((a, b) => a.time - b.time);
}

/** `hh:mm:ss.mmm` — the WebVTT timestamp form. Hours may exceed two digits. */
export function vttTimestamp(seconds: number): string {
  const s = Math.max(0, seconds);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = Math.floor(s % 60);
  const ms = Math.round((s - Math.floor(s)) * 1000);
  const two = (n: number) => String(n).padStart(2, "0");
  return `${two(h)}:${two(m)}:${two(sec)}.${String(ms).padStart(3, "0")}`;
}

/** How far past the last chapter its cue runs when the duration is unknown:
 *  a day, so the cue is active until the video ends whatever its length. */
export const OPEN_ENDED_TAIL_SECONDS = 24 * 3600;

/**
 * The chapters as a WebVTT document. Each cue runs from its chapter to the
 * next; the last runs to the duration when known, else a day. Cue text is
 * escaped — `<` opens a cue tag in WebVTT and `&` an entity — so a chapter
 * named "Q&A <live>" reads back as typed. Empty input yields null: no track is
 * better than an empty one (the player would show an empty menu).
 */
export function chaptersToVtt(
  chapters: readonly RawChapter[] | null | undefined,
  duration?: number | null,
): string | null {
  const list = normalizeChapters(chapters, duration);
  if (list.length === 0) return null;
  const max = knownDuration(duration);
  const lines = ["WEBVTT", ""];
  list.forEach((c, i) => {
    const next = list[i + 1];
    const end = next ? next.time : max !== null ? max : c.time + OPEN_ENDED_TAIL_SECONDS;
    lines.push(
      String(i + 1),
      `${vttTimestamp(c.time)} --> ${vttTimestamp(end)}`,
      escapeCue(c.name),
      "",
    );
  });
  return lines.join("\n");
}

function escapeCue(text: string): string {
  return text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

/**
 * Which chapter the playhead is in: the last one that has started, or -1
 * before the first (and for an unknown time). A chapter list highlights this
 * row as the player plays.
 */
export function activeChapterIndex(
  chapters: readonly Chapter[],
  currentTime: number | null | undefined,
): number {
  if (typeof currentTime !== "number" || !Number.isFinite(currentTime)) return -1;
  let active = -1;
  for (let i = 0; i < chapters.length; i++) {
    if (chapters[i]!.time <= currentTime) active = i;
    else break;
  }
  return active;
}

/** A chapter's start as a timestamp — `m:ss`, or `h:mm:ss` from an hour up.
 *  A position, not a length, so not `formatDuration`. */
export function formatTimestamp(seconds: number | null | undefined): string {
  const s = Math.max(0, Math.floor(seconds ?? 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const two = (n: number) => String(n).padStart(2, "0");
  return h > 0 ? `${h}:${two(m)}:${two(sec)}` : `${m}:${two(sec)}`;
}
